import { supabase } from '../lib/supabase';
import { normalizePhoneForStorage, parsePhoneNumber } from './countryCodes';
import { PROFILE_PHONE_LOOKUP_STAFF_ROLES } from './profilePhoneLookupRoles';
import { isSaasPlatform } from './companyBranding';
import { isPlaceholderCustomerEmail } from './customerEmail';
import {
  buildGuestPlaceholderEmail,
  resolvePlaceholderEmailDomains,
  buildTbProfileGuestEmail,
  type PlaceholderEmailDomains,
} from '../saas/lib/placeholderCustomerEmail';

// Helper to generate UUID (works in browser and Node.js)
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Profile Manager Utility
 *
 * Handles automatic profile creation for phone-based bookings.
 * Creates guest profiles with auto-generated emails when needed.
 */

export interface ProfileData {
  phone: string;
  firstName: string;
  lastName: string;
  email?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  organizationId?: string;
  organizationSlug?: string;
}

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  address?: string;
  city?: string;
  zip_code?: string;
  has_real_email: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  organization_id?: string | null;
}

/**
 * Generate a guest email from phone number (platform-configurable on SaaS).
 */
export function buildProfileGuestEmail(phone: string, domains?: PlaceholderEmailDomains): string {
  const digits = phone.replace(/\D/g, '') || `customer_${Date.now()}`;
  if (isSaasPlatform()) {
    return buildGuestPlaceholderEmail(digits, domains ?? resolvePlaceholderEmailDomains());
  }
  return buildTbProfileGuestEmail(phone);
}

function generateGuestEmail(phone: string): string {
  return buildProfileGuestEmail(phone);
}

async function createProfileViaEdgeFunction(
  payload: {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
    address?: string;
    city?: string;
    zipCode?: string;
    organizationId?: string;
    organizationSlug?: string;
  },
  options?: { accessToken?: string },
): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase configuration missing');
  }

  const authToken = options?.accessToken ?? supabaseAnonKey;

  const response = await fetch(`${supabaseUrl}/functions/v1/create-profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify({
      phone: payload.phone,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      address: payload.address,
      city: payload.city,
      zipCode: payload.zipCode,
      organizationId: payload.organizationId,
      organizationSlug: payload.organizationSlug,
      orgSlug: payload.organizationSlug,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Failed to create profile: ${response.status}`);
  }

  const result = await response.json();
  return result.id as string;
}

/**
 * Get or create a customer profile by phone number
 *
 * If the phone exists, returns the existing profile.
 * If new, creates a profile with guest email.
 *
 * @param data - Profile data including phone (required)
 * @returns Profile ID
 */
export async function getOrCreateProfile(data: ProfileData): Promise<string> {
  // Parse and normalize phone number
  // Phone might come as "+919876543210" (full format) or "9876543210" (digits only)
  let phoneInput = data.phone || '';

  // If phone doesn't start with +, try to parse it or use default country code
  if (!phoneInput.startsWith('+')) {
    // Try parsing - might detect country code
    const parsed = parsePhoneNumber(phoneInput);
    if (parsed.countryCode && parsed.phoneNumber) {
      phoneInput = `${parsed.countryCode}${parsed.phoneNumber}`;
    } else {
      // Use default country code for India
      const digitsOnly = phoneInput.replace(/\D/g, '');
      if (digitsOnly.length >= 10) {
        phoneInput = `+91${digitsOnly}`;
      }
    }
  }

  // Parse the phone number to get country code and phone number separately
  const parsed = parsePhoneNumber(phoneInput);

  if (!parsed.phoneNumber || parsed.phoneNumber.length < 7) {
    const digitsOnly = phoneInput.replace(/\D/g, '');
    throw new Error(
      `Valid phone number is required (minimum 7 digits). Provided: ${digitsOnly.length} digits`,
    );
  }

  const normalizedPhone = normalizePhoneForStorage(parsed.countryCode || '+91', parsed.phoneNumber);

  if (!normalizedPhone || normalizedPhone.trim() === '') {
    throw new Error('Failed to normalize phone number. Please check the format.');
  }

  // Check if profile exists - first try customer role, then any role
  // This handles the case where the phone is registered with a different role
  const { data: existingCustomerProfile, error: searchError } = await supabase
    .from('profiles')
    .select('id, phone, email, first_name, last_name, role')
    .eq('phone', normalizedPhone)
    .eq('role', 'customer')
    .maybeSingle();

  if (searchError && searchError.code !== 'PGRST116') {
    console.error('Error searching for customer profile:', searchError);
  }

  // If customer profile exists, return it
  if (existingCustomerProfile) {
    console.log('Found existing customer profile:', existingCustomerProfile.id);
    return existingCustomerProfile.id;
  }

  // Check if profile exists with ANY role (might be staff, admin, etc.)
  const { data: existingAnyProfile, error: anySearchError } = await supabase
    .from('profiles')
    .select('id, phone, email, first_name, last_name, role')
    .eq('phone', normalizedPhone)
    .maybeSingle();

  if (anySearchError && anySearchError.code !== 'PGRST116') {
    console.error('Error searching for any profile:', anySearchError);
  }

  // If any profile with this phone exists, return it (avoids duplicate key error)
  if (existingAnyProfile) {
    console.log('Found existing profile with role:', existingAnyProfile.role, '- ID:', existingAnyProfile.id);
    return existingAnyProfile.id;
  }

  // Create new profile
  console.log('Creating new profile for phone:', normalizedPhone);

  // Determine email - use provided or generate guest email
  const email = data.email && data.email.includes('@') ? data.email : generateGuestEmail(normalizedPhone);

  const hasRealEmail = !!(data.email && !isPlaceholderCustomerEmail(data.email));

  // Check if we have a Supabase session
  const {
    data: { session },
  } = await supabase.auth.getSession();

  // If no session (guest user), use Edge Function to bypass RLS
  if (!session) {
    console.log('No Supabase session, using Edge Function to create profile');
    const id = await createProfileViaEdgeFunction({
      phone: normalizedPhone,
      firstName: data.firstName || 'Customer',
      lastName: data.lastName || '',
      email,
      address: data.address,
      city: data.city,
      zipCode: data.zipCode,
      organizationId: data.organizationId,
      organizationSlug: data.organizationSlug,
    });
    console.log('Profile created via Edge Function:', id);
    return id;
  }

  // For authenticated users, use direct Supabase insert
  // Generate UUID for profile
  const profileId = generateUUID();

  const profileData = {
    id: profileId,
    email: email,
    first_name: data.firstName || 'Customer',
    last_name: data.lastName || '',
    phone: normalizedPhone,
    role: 'customer',
    address: data.address || null,
    city: data.city || null,
    zip_code: data.zipCode || null,
    has_real_email: hasRealEmail,
    is_active: true,
    organization_id: data.organizationId || null,
  };

  const { data: newProfile, error: createError } = await supabase
    .from('profiles')
    .insert(profileData)
    .select()
    .single();

  if (createError) {
    console.error('Error creating profile:', createError);

    // If error is due to duplicate phone (race condition), try to fetch again
    if (createError.message?.includes('duplicate') || createError.code === '23505') {
      const { data: retryProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone', normalizedPhone)
        .eq('role', 'customer')
        .single();

      if (retryProfile) {
        console.log('Profile created by another request:', retryProfile.id);
        return retryProfile.id;
      }
    }

    // Admin/staff sessions often hit RLS on direct insert — use service-role edge function
    console.warn('Direct profile insert failed, falling back to edge function:', createError.message);
    try {
      const id = await createProfileViaEdgeFunction(
        {
          phone: normalizedPhone,
          firstName: data.firstName || 'Customer',
          lastName: data.lastName || '',
          email,
          address: data.address,
          city: data.city,
          zipCode: data.zipCode,
          organizationId: data.organizationId,
          organizationSlug: data.organizationSlug,
        },
        { accessToken: session.access_token },
      );
      console.log('Profile created via Edge Function fallback:', id);
      return id;
    } catch (edgeErr) {
      console.error('Edge Function fallback failed:', edgeErr);
      throw new Error(`Failed to create profile: ${createError.message || (edgeErr as Error).message}`);
    }
  }

  console.log('Created new profile:', newProfile.id);
  return newProfile.id;
}

/**
 * Get profile by phone number
 * Checks both customer and staff roles
 */
export async function getProfileByPhone(
  phone: string,
  options?: { organizationId?: string },
): Promise<Profile | null> {
  const organizationId = options?.organizationId;
  // Parse phone number first
  const parsed = parsePhoneNumber(phone);
  const normalizedPhone = normalizePhoneForStorage(
    parsed.countryCode || '+91',
    parsed.phoneNumber || phone.replace(/\D/g, ''),
  );
  const phoneDigits = (parsed.phoneNumber || phone.replace(/\D/g, '')).slice(-12);

  // First try customer role
  let customerQuery = supabase
    .from('profiles')
    .select('*')
    .eq('phone', normalizedPhone)
    .eq('role', 'customer');

  if (organizationId) {
    customerQuery = customerQuery.eq('organization_id', organizationId);
  }

  const { data: customerData, error: customerError } = await customerQuery.maybeSingle();

  if (customerData) {
    return customerData;
  }

  if (customerError && customerError.code !== 'PGRST116') {
    console.error('Error fetching customer profile by phone:', customerError);
  }

  // Attempt loose match for legacy data (numbers stored with spaces/different formats)
  if (phoneDigits.length >= 6) {
    let looseCustomerQuery = supabase
      .from('profiles')
      .select('*')
      .eq('role', 'customer')
      .ilike('phone', `%${phoneDigits}%`);

    if (organizationId) {
      looseCustomerQuery = looseCustomerQuery.eq('organization_id', organizationId);
    }

    const { data: looseCustomer, error: looseCustomerError } = await looseCustomerQuery.maybeSingle();

    if (looseCustomer) {
      return looseCustomer;
    }

    if (looseCustomerError && looseCustomerError.code !== 'PGRST116') {
      console.warn('Loose customer lookup error:', looseCustomerError);
    }
  }

  // If not found as customer, try staff / org member roles
  let staffQuery = supabase
    .from('profiles')
    .select('*')
    .eq('phone', normalizedPhone)
    .in('role', [...PROFILE_PHONE_LOOKUP_STAFF_ROLES]);

  if (organizationId) {
    staffQuery = staffQuery.eq('organization_id', organizationId);
  }

  const { data: staffData, error: staffError } = await staffQuery.maybeSingle();

  if (staffData) {
    return staffData;
  }

  if (phoneDigits.length >= 6) {
    let looseStaffQuery = supabase
      .from('profiles')
      .select('*')
      .in('role', [...PROFILE_PHONE_LOOKUP_STAFF_ROLES])
      .ilike('phone', `%${phoneDigits}%`);

    if (organizationId) {
      looseStaffQuery = looseStaffQuery.eq('organization_id', organizationId);
    }

    const { data: looseStaff, error: looseStaffError } = await looseStaffQuery.maybeSingle();

    if (looseStaff) {
      return looseStaff;
    }

    if (looseStaffError && looseStaffError.code !== 'PGRST116') {
      console.warn('Loose staff lookup error:', looseStaffError);
    }
  }

  if (staffError && staffError.code !== 'PGRST116') {
    console.error('Error fetching staff profile by phone:', staffError);
  }

  return null;
}

/**
 * Get profile by ID
 */
export async function getProfileById(
  profileId: string,
  options?: { organizationId?: string | null },
): Promise<Profile | null> {
  let query = supabase.from('profiles').select('*').eq('id', profileId);

  if (options?.organizationId) {
    query = query.eq('organization_id', options.organizationId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error('Error fetching profile by ID:', error);
    return null;
  }

  return data ?? null;
}

/**
 * Update profile with real email
 * Called when customer provides their email later
 */
export async function updateProfileEmail(profileId: string, email: string): Promise<void> {
  if (!email || !email.includes('@')) {
    throw new Error('Valid email is required');
  }

  const { error } = await supabase
    .from('profiles')
    .update({
      email: email,
      has_real_email: true,
      updated_at: new Date().toISOString(),
    })
    .eq('id', profileId);

  if (error) {
    console.error('Error updating profile email:', error);
    throw new Error('Failed to update email');
  }
}

/**
 * Update profile details
 */
export async function updateProfile(profileId: string, updates: Partial<ProfileData>): Promise<void> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (updates.firstName) updateData.first_name = updates.firstName;
  if (updates.lastName) updateData.last_name = updates.lastName;
  if (updates.address) updateData.address = updates.address;
  if (updates.city) updateData.city = updates.city;
  if (updates.zipCode) updateData.zip_code = updates.zipCode;

  if (updates.email && updates.email.includes('@')) {
    updateData.email = updates.email;
    updateData.has_real_email = !isPlaceholderCustomerEmail(updates.email);
  }

  const { error } = await supabase.from('profiles').update(updateData).eq('id', profileId);

  if (error) {
    console.error('Error updating profile:', error);
    throw new Error('Failed to update profile');
  }
}

/**
 * Check if phone number already has a profile
 */
export async function phoneExists(phone: string): Promise<boolean> {
  // Parse phone number first
  const parsed = parsePhoneNumber(phone);
  const normalizedPhone = normalizePhoneForStorage(
    parsed.countryCode || '+91',
    parsed.phoneNumber || phone.replace(/\D/g, ''),
  );

  const { count, error } = await supabase
    .from('profiles')
    .select('id', { count: 'exact', head: true })
    .eq('phone', normalizedPhone)
    .eq('role', 'customer');

  if (error) {
    console.error('Error checking phone existence:', error);
    return false;
  }

  return (count || 0) > 0;
}

/**
 * Get or create profile from enquiry data
 * Special helper for admin direct booking from enquiry
 */
export async function getOrCreateProfileFromEnquiry(enquiry: {
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  location?: string;
}): Promise<string> {
  const nameParts = enquiry.customer_name.trim().split(' ');
  const firstName = nameParts[0] || 'Customer';
  const lastName = nameParts.slice(1).join(' ') || '';

  return getOrCreateProfile({
    phone: enquiry.customer_phone,
    firstName,
    lastName,
    email: enquiry.customer_email,
    address: enquiry.location,
  });
}
